<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Add New Seller</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Create a new seller account with basic information and address details
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-store</v-icon>
                      Seller Registration
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.push('/sellers')"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Sellers
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Store Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-store</v-icon>
                    Store Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.gst_no"
                        label="GST Number"
                        placeholder="Enter GST number"
                        outlined
                        :error-messages="errors.gst_no"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-file-document</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.store_name"
                        label="Seller Name"
                        placeholder="Enter seller name"
                        outlined
                        :error-messages="errors.store_name"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-store</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.drug_license_no"
                        label="Drug License Number"
                        placeholder="Enter drug license number"
                        outlined
                        :error-messages="errors.drug_license_no"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-medical-bag</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.store_type_id"
                        :items="storeTypeItems"
                        label="Store Type"
                        placeholder="Select store type"
                        outlined
                        :error-messages="errors.store_type_id"
                        class="professional-field"
                        hide-details="auto"
                        item-text="description"
                        item-value="id"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-tag</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <!-- Basic Information Section (Seller Information) -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-account</v-icon>
                    Basic Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.first_name"
                        label="First Name *"
                        placeholder="Enter first name"
                        outlined
                        :rules="[(v) => !!v || 'First name is required']"
                        :error-messages="errors.first_name"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.middle_name"
                        label="Middle Name (Optional)"
                        placeholder="Enter middle name"
                        outlined
                        :error-messages="errors.middle_name"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.last_name"
                        label="Last Name *"
                        placeholder="Enter last name"
                        outlined
                        :rules="[(v) => !!v || 'Last name is required']"
                        :error-messages="errors.last_name"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.phone"
                        label="Phone Number *"
                        placeholder="Enter 10-digit phone number"
                        outlined
                        :rules="[
                          (v) => !!v || 'Phone number is required',
                          (v) => /^\d{10}$/.test(v) || 'Phone number must be exactly 10 digits',
                        ]"
                        :error-messages="errors.phone"
                        class="professional-field"
                        hide-details="auto"
                        maxlength="10"
                        @keypress="allowOnlyDigits"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-phone</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.email"
                        label="Email Address"
                        placeholder="Enter email address"
                        outlined
                        :rules="[
                          (v) => !v || /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(v) || 'Please enter a valid email address',
                        ]"
                        @input="errors.email = ''"
                        :error-messages="errors.email"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-email</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>


                <!-- Store Address Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-store-marker</v-icon>
                    Store Address Information
                  </h3>
                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          v-model="form.store_address"
                          label="Store Full Address"
                          placeholder="Enter complete store address with landmark"
                          outlined
                          rows="3"
                          :error-messages="errors.store_address"
                          class="professional-field"
                          hide-details="auto"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-store</v-icon>
                          </template>
                        </v-textarea>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" md="4">
                        <v-autocomplete
                          v-model="form.store_state"
                          :items="storeStateItems"
                          label="Store State"
                          placeholder="Select store state"
                          outlined
                          @change="onStoreStateChange"
                          :error-messages="errors.store_state"
                          class="professional-field"
                          hide-details="auto"
                          clearable
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-map</v-icon>
                          </template>
                        </v-autocomplete>
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="form.store_city"
                          label="Store City"
                          placeholder="Enter store city"
                          outlined
                          :error-messages="errors.store_city"
                          class="professional-field"
                          hide-details="auto"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-city</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="form.store_pincode"
                          label="Store Pincode"
                          placeholder="Enter store pincode"
                          type="number"
                          outlined
                          :error-messages="errors.store_pincode"
                          class="professional-field"
                          hide-details="auto"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-mailbox</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                </div>

                <!-- Pickup Address Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-truck-delivery</v-icon>
                    Pickup Address Information *
                  </h3>
                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          v-model="form.address"
                          label="Pickup Full Address *"
                          placeholder="Enter complete pickup address with landmark"
                          outlined
                          rows="3"
                          :rules="[(v) => !!v || 'Pickup address is required']"
                          :error-messages="errors.address"
                          class="professional-field"
                          hide-details="auto"
                          required
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-home</v-icon>
                          </template>
                        </v-textarea>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" md="4">
                        <v-autocomplete
                          v-model="form.state"
                          :items="stateItems"
                          label="Pickup State *"
                          placeholder="Select pickup state"
                          outlined
                          :rules="[(v) => !!v || 'Pickup state is required']"
                          @change="onStateChange"
                          :loading="statesLoading"
                          :error-messages="errors.state"
                          class="professional-field"
                          hide-details="auto"
                          required
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-map</v-icon>
                          </template>
                        </v-autocomplete>
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="form.city"
                          label="Pickup City *"
                          placeholder="Enter pickup city"
                          outlined
                          :rules="[(v) => !!v || 'Pickup city is required']"
                          :error-messages="errors.city"
                          class="professional-field"
                          hide-details="auto"
                          required
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-city</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="form.pincode"
                          label="Pickup Pincode *"
                          placeholder="Enter pickup pincode"
                          type="number"
                          outlined
                          :rules="[(v) => !!v || 'Pickup pincode is required']"
                          :error-messages="errors.pincode"
                          class="professional-field"
                          hide-details="auto"
                          required
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-mailbox</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                </div>

                <!-- Optional Banking Information Section -->
                <v-expansion-panels class="mb-2">
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="d-flex align-center">
                        <v-icon color="primary" class="mr-2">mdi-bank</v-icon>
                        <span class="section-title">Banking Information (Optional)</span>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <p class="text-caption grey--text mb-3">
                        Banking details can be added now or updated later from the seller's profile.
                      </p>
                      
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="form.bank_account_name"
                            label="Account Holder Name"
                            placeholder="Enter account holder name"
                            outlined
                            :error-messages="errors.bank_account_name"
                            class="professional-field"
                            hide-details="auto"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon color="grey">mdi-account</v-icon>
                            </template>
                          </v-text-field>
                        </v-col>

                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="form.bank_account_number"
                            label="Account Number"
                            placeholder="Enter bank account number"
                            outlined
                            :error-messages="errors.bank_account_number"
                            class="professional-field"
                            hide-details="auto"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon color="grey">mdi-credit-card</v-icon>
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="form.bank_bank_name"
                            label="Bank Name"
                            placeholder="Enter bank name"
                            outlined
                            :error-messages="errors.bank_bank_name"
                            class="professional-field"
                            hide-details="auto"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon color="grey">mdi-bank</v-icon>
                            </template>
                          </v-text-field>
                        </v-col>

                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="form.bank_branch"
                            label="Branch Name"
                            placeholder="Enter branch name"
                            outlined
                            :error-messages="errors.bank_branch"
                            class="professional-field"
                            hide-details="auto"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon color="grey">mdi-source-branch</v-icon>
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="form.bank_ifsc_code"
                            label="IFSC Code"
                            placeholder="Enter IFSC code"
                            outlined
                            :error-messages="errors.bank_ifsc_code"
                            class="professional-field"
                            hide-details="auto"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon color="grey">mdi-identifier</v-icon>
                            </template>
                          </v-text-field>
                        </v-col>

                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="form.bank_micr_code"
                            label="MICR Code (Optional)"
                            placeholder="Enter MICR code"
                            outlined
                            :error-messages="errors.bank_micr_code"
                            class="professional-field"
                            hide-details="auto"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon color="grey">mdi-barcode</v-icon>
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12" md="6">
                          <v-select
                            v-model="form.bank_account_type_id"
                            :items="accountTypeItems"
                            item-text="description"
                            item-value="id"
                            label="Account Type (Optional)"
                            placeholder="Select account type"
                            outlined
                            clearable
                            :error-messages="errors.bank_account_type_id"
                            class="professional-field"
                            hide-details="auto"
                            prepend-inner-icon="mdi-bank-outline"
                          ></v-select>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>

              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{
                          hasValidationErrors()
                            ? "mdi-alert-circle"
                            : "mdi-check-circle"
                        }}
                      </v-icon>
                      {{
                        hasValidationErrors()
                          ? "Please fix validation errors"
                          : "Form validation passed"
                      }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields including name, phone and address must be completed
                    </span>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.push('/sellers')"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="save"
                      :loading="isLoading"
                      :disabled="isLoading || !valid"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Save Seller
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "SellerCreatePage",
  data() {
    return {
      valid: false,
      form: {
        first_name: "",
        middle_name: "",
        last_name: "",
        email: "",
        phone: "",
        // Personal Address
        address: "",
        city: "",
        state: "",
        pincode: "",
        // Store Address
        store_address: "",
        store_city: "",
        store_state: "",
        store_pincode: "",
        password: "123456",
        position_id: null, // Will be set to SELLER position
        is_active: true,
        password_confirmation: "123456",
        soft_password: "123456",
        // Store Information
        gst_no: "",
        store_name: "",
        drug_license_no: "",
        store_type_id: "",
        // Optional Banking Details
        bank_account_name: "",
        bank_account_number: "",
        bank_bank_name: "",
        bank_branch: "",
        bank_ifsc_code: "",
        bank_micr_code: "",
        bank_account_type_id: null
      },
      errors: {},
      isLoading: false,
      showPersonalAddress: true,
      statesLoading: false,
      citiesLoading: false,
      storeStatesLoading: false,
      storeCitiesLoading: false,
      stateItems: [],
      cityItems: [],
      storeStateItems: [],
      storeCityItems: [],
      positionItems: [],
      storeTypeItems: [],
      accountTypeItems: [],
    };
  },
  mounted() {
    this.form.company_id = this.company.id;
    this.form.role_id = "3";
    this.getMasters();
    this.loadAllStates();
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get(`masters?context=user`);
        const masters = response.data;
        
        // Find SELLER position
        const sellerPosition = masters.positions.find(
          position => position.name.toUpperCase() === 'SELLER'
        );
        
        if (sellerPosition) {
          this.form.position_id = sellerPosition.id;
        }
        
        
        masters.positions.forEach((position) => {
          this.positionItems.push({
            id: position.id,
            text: position.name,
            value: position.id,
          });
        });
        
        if (masters.store_types) {
          this.storeTypeItems = masters.store_types.map(storeType => ({
            id: storeType.id,
            description: storeType.description,
            value: storeType.id
          }));
        }

        const bankRes = await axios.get(`masters?context=bank_details`);
        if (bankRes.data.account_types) {
          this.accountTypeItems = bankRes.data.account_types;
        }
      } catch (error) {
        console.log(error);
        window.location.href = "/auth/login";
      }
    },

    async loadAllStates() {
      this.statesLoading = true;
      this.storeStatesLoading = true;
      try {
        const response = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country: 'India' })
        });
        const json = await response.json();
        const stateList = json.data.states.map(s => ({ text: s.name, value: s.name }));
        this.stateItems = stateList;
        this.storeStateItems = stateList;
      } catch (error) {
        console.error('Error loading states:', error);
        const fallback = [
          'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh',
          'Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka',
          'Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram',
          'Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
          'Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
          'Andaman and Nicobar Islands','Chandigarh','Dadra and Nagar Haveli and Daman and Diu',
          'Delhi','Jammu and Kashmir','Ladakh','Lakshadweep','Puducherry'
        ].map(name => ({ text: name, value: name }));
        this.stateItems = fallback;
        this.storeStateItems = fallback;
      } finally {
        this.statesLoading = false;
        this.storeStatesLoading = false;
      }
    },

    async onStateChange() {
      if (this.form.state) {
        await this.loadCitiesForState(this.form.state, 'pickup');
      }
    },

    async onStoreStateChange() {
      if (this.form.store_state) {
        await this.loadCitiesForState(this.form.store_state, 'store');
      }
    },

    async loadCitiesForState(stateName, type) {
      const loadingKey = type === 'store' ? 'storeCitiesLoading' : 'citiesLoading';
      const itemsKey = type === 'store' ? 'storeCityItems' : 'cityItems';
      this[loadingKey] = true;
      try {
        const response = await fetch('https://countriesnow.space/api/v0.1/countries/state/cities', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country: 'India', state: stateName })
        });
        const json = await response.json();
        this[itemsKey] = (json.data || []).map(city => ({ text: city, value: city }));
      } catch (error) {
        console.error('Error loading cities:', error);
        this[itemsKey] = [];
      } finally {
        this[loadingKey] = false;
      }
    },


    allowOnlyDigits(event) {
      if (!/\d/.test(event.key)) {
        event.preventDefault();
      }
    },

    hasValidationErrors() {
      return (
        Object.keys(this.errors).length > 0 ||
        !this.valid
      );
    },


    async save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        this.isLoading = true;
        this.errors = {};

        // Create FormData for potential file uploads
        const formData = new FormData();

        // Add basic form fields
        Object.keys(this.form).forEach((key) => {
          formData.append(key, this.form[key]);
        });

        await axios.post("/users/create_seller", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.isLoading = false;
        await this.saveUserTimestamp(null, this.form);
        this.$toast.success('Seller account created successfully');
        this.$router.push("/sellers");
      } catch (error) {
        this.isLoading = false;
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          console.error("Error creating seller:", error);
          this.$toast.error('Failed to create seller account');
        }
      }
    },
  },
};
</script>

<style scoped>
/* Professional Form Card with Integrated Header */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

</style>